import React from 'react';
import classnames from 'classnames/bind';
import { FormProvider } from 'react-hook-form';
import { useToggle } from '@mantine/hooks';

import { Deployment } from 'types/deployment';
import { VideoSource } from 'hooks/api/useVideoSources';

import { isOnDemandGateway } from 'services/gateway';
import { useGateway } from 'gateways/hooks/useGateway';
import { useMqttGatewaysState } from 'gateways/hooks/useMqttGatewaysState';
import { usePipeline } from 'pipelines/hooks/usePipeline';
import { useStartCloudGateway } from 'gateways/hooks/useStartCloudGateway';
import { useUpdateDeployment } from 'deployments/hooks/useUpdateDeployment';

import { ActivePipelineContext } from 'pipelines/context/active_pipeline';
import { Button } from 'components/Button/Button';

import { DeploymentFormActions } from './Actions';
import { DeploymentFormContent, DeploymentFormContentRef } from './Content';
import { DeploymentFormErrorMessage } from './DeploymentFormErrorMessage';
import { DeploymentFormProvider } from './context/deployment_form_provider';
import { useDeploymentFormContent } from './useDeploymentFormContent';
import styles from './Form.module.scss';

const c = classnames.bind(styles);

export type EditDeploymentsFormProps = {
  className?: string;
  deployment: Deployment;
  startInEditMode?: boolean;
  onSuccess?: () => void;
  canStartOnDeploy?: boolean;
  allowedCameraIds?: string[];
  allowedStreamIds?: string[];
};

export function EditDeploymentForm({
  className,
  deployment,
  startInEditMode = false,
  onSuccess,
  canStartOnDeploy = true,
  allowedCameraIds,
  allowedStreamIds,
}: EditDeploymentsFormProps) {
  const { data: gateway, isLoading: isLoadingGateway } = useGateway(
    deployment.gateway_id
  );
  const { data: pipeline, isLoading: isLoadingPipeline } = usePipeline(
    deployment.pipeline_id
  );
  const { mutate: startCloudGateway, isLoading: isStartingCloudGateway } =
    useStartCloudGateway();

  // Subscribe to MQTT updates for the deployment's gateway
  useMqttGatewaysState(deployment.gateway_id ? [deployment.gateway_id] : []);

  const [isEditing, toggleIsEditing] = useToggle([
    startInEditMode,
    !startInEditMode,
  ]);

  const shouldStartImmediatelyDefault =
    canStartOnDeploy && gateway ? gateway.status !== 'offline' : false;
  const [shouldStartImmediately, toggleShouldStartImmediately] = useToggle([
    shouldStartImmediatelyDefault,
    !shouldStartImmediatelyDefault,
  ]);

  const formContentRef = React.useRef<DeploymentFormContentRef>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);

  const {
    mutate: updateDeployment,
    isLoading,
    error,
  } = useUpdateDeployment({
    onError: () => containerRef.current?.scrollIntoView(),
    onSuccess() {
      toggleIsEditing(false);
      onSuccess?.();
    },
  });

  async function handleSubmit() {
    if (!formContentRef.current || !pipeline || !gateway) {
      return;
    }

    const { configuration } = await formContentRef.current.onSubmit();

    updateDeployment({
      id: deployment.id,
      state: shouldStartImmediately ? 'running' : 'stopped',
      configuration,
    });
  }

  // Auto start an ondemand gateway if it's offline
  React.useEffect(() => {
    if (
      gateway &&
      gateway.is_managed &&
      isOnDemandGateway(gateway.model) &&
      gateway.status !== 'online' &&
      gateway.status !== 'starting' &&
      !isStartingCloudGateway
    ) {
      startCloudGateway(gateway.id);
    }
  }, [gateway, startCloudGateway, isStartingCloudGateway]);

  if (isLoadingGateway || isLoadingPipeline) {
    return <>Loading...</>;
  }

  if (!pipeline || !gateway) {
    return null;
  }

  return (
    <div className={c('container', className)} ref={containerRef}>
      {!startInEditMode && (
        <div className={c('edit-form-header')}>
          <Button
            variant={isEditing ? 'secondary' : 'primary'}
            size="small"
            onClick={() => toggleIsEditing()}
          >
            {isEditing ? 'Cancel' : 'Edit configuration'}
          </Button>
        </div>
      )}
      <section className={c('body')}>
        <ActivePipelineContext.Provider value={pipeline}>
          <section
            className={c('edit-form')}
            aria-label={`Configure parameters for deployment ${deployment.name}`}
          >
            {error && <DeploymentFormErrorMessage error={error} />}

            <DeploymentFormProvider
              pipeline={pipeline}
              gateway={gateway}
              deployment={deployment}
              readonly={!isEditing}
              allowedCameraIds={allowedCameraIds}
              allowedStreamIds={allowedStreamIds}
            >
              <FormContent key={String(isEditing)} deployment={deployment}>
                <DeploymentFormContent ref={formContentRef} />
              </FormContent>
            </DeploymentFormProvider>
          </section>
        </ActivePipelineContext.Provider>
      </section>
      {isEditing && (
        <DeploymentFormActions
          className={c('edit-form-actions')}
          pipeline={pipeline}
          gateway={gateway}
          onCancel={
            startInEditMode ? onSuccess : () => toggleIsEditing(false)
          }
          onSubmit={handleSubmit}
          isDeploying={isLoading}
          startImmediately={shouldStartImmediately}
          onToggleStartImmediately={toggleShouldStartImmediately}
          canStartOnDeploy={canStartOnDeploy}
        />
      )}
    </div>
  );
}

type FormContentProps = React.PropsWithChildren<{
  deployment: Deployment;
  videoSource?: VideoSource;
}>;

function FormContent({ deployment, videoSource, children }: FormContentProps) {
  const formMethods = useDeploymentFormContent(deployment, videoSource);
  return <FormProvider {...formMethods}>{children}</FormProvider>;
}
